import { useMemo, type FunctionComponent } from "react";
import { FireOutlined, TrophyFilled } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Drawer, Space, Table, Tag, Tooltip, Typography } from "antd";
import dayjs from "dayjs";

import { HabitStatus, type HabitTemplateEntity } from "@fitness-app/data-models/entities/Habit";
import { type Trainee } from "@fitness-app/data-models/entities/Trainee";

import { supabase } from "~/store/initializeStore";
import { HabitIcon } from "./habitIcons";

// Read-only enrollment view for one habit template: who is signed up + a simple ranking
// (completed days, then current streak). No management actions here — assignment/removal
// stay in HabitAssignModal / TraineeDetails.

type EnrollmentRow = {
  id: string;
  status: string;
  currentStreak: number;
  longestStreak: number;
  startDate: string | null;
  lastCompletedDate: string | null;
  trainee: Pick<Trainee, "id" | "firstName" | "lastName" | "email"> | null;
};

type RankedRow = EnrollmentRow & {
  completedCount: number;
  rank: number;
};

const habitStatusLabels: Record<string, string> = {
  [HabitStatus.Active]: "Aktywny",
  [HabitStatus.Inactive]: "Nieaktywny",
  [HabitStatus.Upcoming]: "Nadchodzący",
};

const habitStatusColors: Record<string, string> = {
  [HabitStatus.Active]: "green",
  [HabitStatus.Inactive]: "default",
  [HabitStatus.Upcoming]: "blue",
};

const rankColors: Record<number, string> = {
  1: "#faad14",
  2: "#bfbfbf",
  3: "#d48806",
};

type Props = {
  habit: HabitTemplateEntity | null;
  open: boolean;
  onClose: () => void;
};

const HabitEnrollmentDrawer: FunctionComponent<Props> = ({ habit, open, onClose }) => {
  const enrollmentQuery = useQuery({
    queryKey: ["habit-enrollment", habit?.id],
    enabled: open && Boolean(habit?.id),
    queryFn: async () => {
      const { data: clientHabits, error } = await supabase
        .from("client_habit")
        .select(
          "id, status, currentStreak, longestStreak, startDate, lastCompletedDate, trainee:traineeId(id, firstName, lastName, email)",
        )
        .eq("templateId", habit!.id)
        .returns<EnrollmentRow[]>();

      if (error) {
        throw error;
      }

      const rows = clientHabits ?? [];
      if (!rows.length) {
        return [];
      }

      // One grouped aggregate (RPC) instead of a per-enrollee count fan-out — exact and cheap,
      // no PostgREST row-cap undercount.
      const { data: counts, error: countsError } = await supabase.rpc("habit_template_completed_counts", {
        p_template: habit!.id,
      });

      if (countsError) {
        throw countsError;
      }

      const countByHabit = new Map((counts ?? []).map((row) => [row.clientHabitId, Number(row.completed)]));

      return rows.map((row) => ({ ...row, completedCount: countByHabit.get(row.id) ?? 0 }));
    },
  });

  // Ranking: completed days desc, then current streak desc; ties share a dense rank.
  const ranked = useMemo<RankedRow[]>(() => {
    const rows = [...(enrollmentQuery.data ?? [])].sort(
      (a, b) => b.completedCount - a.completedCount || b.currentStreak - a.currentStreak,
    );

    let rank = 0;
    let previousKey: string | null = null;
    return rows.map((row) => {
      const key = `${row.completedCount}:${row.currentStreak}`;
      if (previousKey === null || key !== previousKey) {
        rank += 1;
        previousKey = key;
      }
      return { ...row, rank };
    });
  }, [enrollmentQuery.data]);

  return (
    <Drawer
      title={
        <Space direction="vertical" size={0}>
          <Space size={8}>
            <HabitIcon iconKey={habit?.icon} size={16} />
            <span>Zapisane klientki — {habit?.name}</span>
          </Space>
          <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
            Ranking wg wykonanych dni (podgląd — zarządzanie przez „Przypisz" i profil klientki)
          </Typography.Text>
        </Space>
      }
      width={760}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      <Table<RankedRow>
        dataSource={ranked}
        rowKey="id"
        size="middle"
        scroll={{ x: true }}
        loading={enrollmentQuery.isFetching}
        locale={{
          // A failed fetch must not read as "nobody enrolled".
          emptyText: enrollmentQuery.isError
            ? "Nie udało się pobrać zapisanych — spróbuj ponownie"
            : "Nikt jeszcze nie jest tutaj zapisany",
        }}
        pagination={ranked.length > 20 ? { pageSize: 20 } : false}
      >
        <Table.Column<RankedRow>
          title="#"
          key="rank"
          width={64}
          render={(_, row) => (
            <Space size={4}>
              {row.rank <= 3 && row.completedCount > 0 ? (
                <TrophyFilled style={{ color: rankColors[row.rank] }} />
              ) : null}
              <Typography.Text strong>{row.rank}</Typography.Text>
            </Space>
          )}
        />
        <Table.Column<RankedRow>
          title="Klientka"
          key="trainee"
          render={(_, row) => (
            <Space direction="vertical" size={0}>
              {/* nowrap: never break names/emails mid-word — the table scrolls horizontally instead */}
              <Typography.Text strong style={{ whiteSpace: "nowrap" }}>
                {row.trainee?.firstName} {row.trainee?.lastName}
              </Typography.Text>
              <Typography.Text type="secondary" style={{ whiteSpace: "nowrap" }}>
                {row.trainee?.email}
              </Typography.Text>
            </Space>
          )}
        />
        <Table.Column<RankedRow>
          title="Status"
          dataIndex="status"
          key="status"
          width={110}
          render={(status: string) => (
            <Tag color={habitStatusColors[status] ?? "default"}>{habitStatusLabels[status] ?? status}</Tag>
          )}
        />
        <Table.Column<RankedRow>
          title="Wykonane dni"
          dataIndex="completedCount"
          key="completedCount"
          width={120}
          render={(count: number) => <Typography.Text strong>{count}</Typography.Text>}
        />
        <Table.Column<RankedRow>
          title="Seria"
          key="streak"
          width={110}
          render={(_, row) => (
            <Tooltip title={`Najdłuższa seria: ${row.longestStreak}`}>
              <Space size={4}>
                <FireOutlined style={{ color: "#fa541c" }} />
                <Typography.Text>
                  {row.currentStreak} / {row.longestStreak}
                </Typography.Text>
              </Space>
            </Tooltip>
          )}
        />
        <Table.Column<RankedRow>
          title="Ostatnio wykonany"
          dataIndex="lastCompletedDate"
          key="lastCompletedDate"
          width={150}
          render={(date: string | null) => (date ? dayjs(date).format("DD.MM.YYYY") : "—")}
        />
      </Table>
    </Drawer>
  );
};

export default HabitEnrollmentDrawer;
